
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { shippingService, DEFAULT_SHIPPING_METHODS } from '../services/shippingService';
import { ShippingMethod, Store } from '../types';
import { 
  Truck, 
  Plus, 
  Trash2, 
  Save, 
  Check, 
  Clock, 
  DollarSign, 
  Info, 
  ShieldCheck, 
  Settings2,
  X,
  Zap,
  ArrowRight
} from 'lucide-react';

interface AdminShippingProps {
  storeId: string;
}

const AdminShipping: React.FC<AdminShippingProps> = ({ storeId }) => {
  const [methods, setMethods] = useState<ShippingMethod[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState<Partial<ShippingMethod> | null>(null);

  useEffect(() => {
    const store = storage.getStoreConfig(storeId);
    setMethods(store.shippingConfig?.methods || DEFAULT_SHIPPING_METHODS);
  }, [storeId]);

  const handleSave = () => {
    shippingService.updateMethods(storeId, methods);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleToggleActive = (id: string) => {
    setMethods(methods.map(m => m.id === id ? { ...m, isActive: !m.isActive } : m));
  };

  const handleDelete = (id: string) => {
    setMethods(methods.filter(m => m.id !== id));
  };

  const handleAddOrUpdate = () => {
    if (!editingMethod?.name || editingMethod.baseCost === undefined) return;
    
    if (editingMethod.id) {
      setMethods(methods.map(m => m.id === editingMethod.id ? (editingMethod as ShippingMethod) : m));
    } else {
      const newMethod: ShippingMethod = {
        ...(editingMethod as ShippingMethod),
        id: 'ship-' + Date.now(),
        isActive: true
      };
      setMethods([...methods, newMethod]);
    }
    setShowModal(false);
    setEditingMethod(null);
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Shipping Rules</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Dynamic Logistics Control</p>
        </div>
        <div className="flex gap-4">
           <button 
             onClick={() => { setEditingMethod({ name: '', description: '', baseCost: 0, estimatedDays: '3-5 Days' }); setShowModal(true); }}
             className="bg-white border-2 border-gray-100 text-gray-900 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-gray-50 transition-all"
           >
             <Plus className="h-4 w-4" /> Add Method
           </button>
           <button 
             onClick={handleSave}
             className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
           >
             {isSaved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
             {isSaved ? 'Changes Sync\'d' : 'Save Rules'}
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {methods.map((method) => (
              <div key={method.id} className={`bg-white p-8 rounded-[2.5rem] border transition-all flex flex-col md:flex-row justify-between items-center gap-6 ${method.isActive ? 'border-gray-100 shadow-xl' : 'border-gray-100 opacity-50 grayscale shadow-sm'}`}>
                 <div className="flex items-center gap-6">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${method.isActive ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                       <Truck className="h-8 w-8" />
                    </div>
                    <div>
                       <div className="flex items-center gap-2">
                          <h3 className="text-xl font-black text-gray-900">{method.name}</h3>
                          {method.minSubtotalForFree && (
                            <span className="bg-emerald-50 text-emerald-600 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest border border-emerald-100">Free over ${method.minSubtotalForFree}</span>
                          )}
                       </div>
                       <p className="text-sm text-gray-500 font-medium">{method.description}</p>
                       <div className="flex items-center gap-4 mt-2">
                          <span className="flex items-center gap-1.5 text-[10px] font-black text-gray-400 uppercase tracking-widest"><Clock className="h-3 w-3" /> {method.estimatedDays}</span>
                          <span className="flex items-center gap-1.5 text-[10px] font-black text-blue-600 uppercase tracking-widest"><DollarSign className="h-3 w-3" /> Base: ${method.baseCost}</span>
                       </div>
                    </div>
                 </div>

                 <div className="flex items-center gap-3">
                    <button 
                      onClick={() => handleToggleActive(method.id)}
                      className={`px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${method.isActive ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    >
                      {method.isActive ? 'Active' : 'Disabled'}
                    </button>
                    <button 
                      onClick={() => { setEditingMethod(method); setShowModal(true); }}
                      className="p-3 bg-gray-50 text-gray-400 hover:text-blue-600 rounded-xl transition-all"
                    >
                       <Settings2 className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleDelete(method.id)}
                      className="p-3 bg-gray-50 text-gray-400 hover:text-red-600 rounded-xl transition-all"
                    >
                       <Trash2 className="h-5 w-5" />
                    </button>
                 </div>
              </div>
            ))}
         </div>

         <div className="space-y-8">
            <div className="bg-gray-950 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><Zap className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Shipping Strategy</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">Sellers with 'Free Shipping' options see 35% higher conversion rates on Lynqar.</p>
                  <div className="p-6 bg-white/5 rounded-3xl border border-white/10">
                     <p className="text-[10px] font-black uppercase text-blue-400 mb-2">Recommended Setup</p>
                     <ul className="space-y-3">
                        <li className="text-xs font-bold flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> 1 Standard Method</li>
                        <li className="text-xs font-bold flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> 1 Express Priority</li>
                        <li className="text-xs font-bold flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Threshold-based Free Delivery</li>
                     </ul>
                  </div>
               </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 flex items-start gap-4">
               <ShieldCheck className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
               <p className="text-[10px] text-blue-700 font-medium leading-relaxed uppercase tracking-wider">
                  SaaS Rule: 'Drone Delivery' method is only available for Enterprise Tier stores.
               </p>
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">{editingMethod?.id ? 'Edit Method' : 'New Delivery Method'}</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <div className="p-10 space-y-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Display Name</label>
                    <input 
                      value={editingMethod?.name} 
                      onChange={e => setEditingMethod({...editingMethod, name: e.target.value})}
                      placeholder="e.g. Hyper-Fast Air" 
                      className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                    />
                 </div>
                 <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Base Cost ($)</label>
                       <input 
                         type="number"
                         value={editingMethod?.baseCost} 
                         onChange={e => setEditingMethod({...editingMethod, baseCost: parseFloat(e.target.value)})}
                         className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Estimated Days</label>
                       <input 
                         value={editingMethod?.estimatedDays} 
                         onChange={e => setEditingMethod({...editingMethod, estimatedDays: e.target.value})}
                         placeholder="e.g. 1-2 Days" 
                         className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                       />
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Free Shipping Threshold ($) - Optional</label>
                    <input 
                      type="number"
                      value={editingMethod?.minSubtotalForFree || ''} 
                      onChange={e => setEditingMethod({...editingMethod, minSubtotalForFree: e.target.value ? parseFloat(e.target.value) : undefined})}
                      placeholder="e.g. 500 (Leave blank for no free threshold)" 
                      className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" 
                    />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Short Description</label>
                    <textarea 
                      rows={2}
                      value={editingMethod?.description} 
                      onChange={e => setEditingMethod({...editingMethod, description: e.target.value})}
                      className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold resize-none" 
                    />
                 </div>
                 <button onClick={handleAddOrUpdate} className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-blue-700 transition-all">Save Method</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminShipping;
